import React from 'react';
import { VdpElement, CSVData } from '../../types';
import { Ban, Barcode as BarcodeIcon } from 'lucide-react';
import ReactBarcode from 'react-barcode';
import { getStarSvgPath, getPolygonSvgPath, getLineEffectSvgPath, getTriangleSvgPath, getDiamondSvgPath, getHeartSvgPath, getArrowSvgPath, formatCurrencyValue, formatDateValue } from '../../utils/shapeAndFormatUtils';
import { Ean13BarcodeSvg, UpcaBarcodeSvg, DataMatrixSvg, QrCodeSvg, AztecBarcodeSvg, Pdf417BarcodeSvg } from '../../utils/barcodeGenerators';
import { pointsToSvgPath, getAnchorTangent, bezierPointsToSvgPath } from '../../utils/vectorPathUtils';

interface CanvasElementItemProps {
  el: VdpElement;
  isSelected: boolean;
  isPreviewCanvasActive: boolean;
  isHandToolActive: boolean;
  showLivePreview: boolean;
  previewRecordIndex: number;
  csvData: CSVData | null;
  isSingleSelection: boolean;
  onPointerDown: (e: React.PointerEvent, el: VdpElement) => void;
  onContextMenu: (e: React.MouseEvent, elId: string) => void;
  onDoubleClick: (e: React.MouseEvent, el: VdpElement) => void;
  onResizeStart: (e: React.PointerEvent, el: VdpElement, handle?: string) => void;
  onRotateStart: (e: React.PointerEvent, el: VdpElement) => void;
  onUpdateElement: (id: string, updates: Partial<VdpElement>) => void;
  onControlPointDrag?: (e: React.PointerEvent, el: VdpElement, pointIdx: number, type: 'cp1' | 'cp2') => void;
  onAnchorPointDrag?: (e: React.PointerEvent, el: VdpElement, pointIdx: number) => void;
  onToggleAnchorPointType?: (el: VdpElement, pointIdx: number) => void;
  onEnsureAnchorHandles?: (el: VdpElement, pointIdx: number) => void;
  editingTextId?: string | null;
  setEditingTextId?: (id: string | null) => void;
}

const getStrokeDasharray = (lineDash?: string, strokeWidth: number = 1): string => {
  if (!lineDash || lineDash === 'solid' || lineDash === 'none') return 'none';
  if (lineDash === 'dashed') return `${strokeWidth * 3} ${strokeWidth * 2}`;
  if (lineDash === 'dotted') return `${strokeWidth} ${strokeWidth * 2}`;
  return lineDash;
};

const renderArrowheadDefs = (el: VdpElement) => {
  if (!el.arrowheadStart && !el.arrowheadEnd) return null;
  const color = el.borderColor || el.strokeColor || '#000000';
  
  return (
    <defs>
      {el.arrowheadStart && el.arrowheadStart !== 'none' && (
        <marker id={`arrow-start-${el.id}`} markerWidth="10" markerHeight="10" refX="2" refY="5" orient="auto" markerUnits="strokeWidth">
          {el.arrowheadStart === 'arrow' && <path d="M 0 5 L 10 0 L 10 10 z" fill={color} />}
          {el.arrowheadStart === 'circle' && <circle cx="5" cy="5" r="3" fill={color} />}
          {el.arrowheadStart === 'square' && <rect x="2" y="2" width="6" height="6" fill={color} />}
        </marker>
      )}
      {el.arrowheadEnd && el.arrowheadEnd !== 'none' && (
        <marker id={`arrow-end-${el.id}`} markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
          {el.arrowheadEnd === 'arrow' && <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />}
          {el.arrowheadEnd === 'circle' && <circle cx="5" cy="5" r="3" fill={color} />}
          {el.arrowheadEnd === 'square' && <rect x="2" y="2" width="6" height="6" fill={color} />}
        </marker>
      )}
    </defs>
  );
};

export const CanvasElementItem: React.FC<CanvasElementItemProps> = React.memo(({
  el,
  isSelected,
  isPreviewCanvasActive,
  isHandToolActive,
  showLivePreview,
  previewRecordIndex,
  csvData,
  isSingleSelection,
  onPointerDown,
  onContextMenu,
  onDoubleClick,
  onResizeStart,
  onRotateStart,
  onUpdateElement,
  onControlPointDrag,
  onAnchorPointDrag,
  onToggleAnchorPointType,
  onEnsureAnchorHandles,
  editingTextId,
  setEditingTextId}) => {
  const [selectedAnchorIdx, setSelectedAnchorIdx] = React.useState<number | null>(null);
  // Compute text display value
  let displayVal = (showLivePreview && el.columnName && csvData && csvData.rows && csvData.rows[previewRecordIndex] && csvData.rows[previewRecordIndex][el.columnName] !== undefined)
    ? csvData.rows[previewRecordIndex][el.columnName]
    : (el.content || el.text || el.staticValue || (el.columnName ? `[${el.columnName}]` : 'Sample Text'));
  
  // Ensure displayVal is never empty or pure whitespace for text/variable elements so they clearly display placeholder text or content instead of disappearing when unmapped
  if (!displayVal || String(displayVal).trim() === '') {
    displayVal = el.columnName 
      ? `[${el.columnName}]` 
      : (el.type === 'serial' ? 'SN-0001' : (el.type === 'currency' ? '₹99.99' : (el.type === 'date' ? '01/01/2026' : 'Sample Text')));
  }

  if (el.type === 'uppercase' || el.textCase === 'uppercase') {
    displayVal = String(displayVal).toUpperCase();
  } else if (el.textCase === 'lowercase') {
    displayVal = String(displayVal).toLowerCase();
  } else if (el.textCase === 'capitalize') {
    displayVal = String(displayVal).replace(/\b\w/g, l => l.toUpperCase());
  }

  if (el.type === 'currency') {
    displayVal = formatCurrencyValue(
      displayVal,
      el.currencySymbol || '₹',
      el.decimalPlaces ?? 2,
      el.formatThousands ?? true
    );
  } else if (el.type === 'date') {
    displayVal = formatDateValue(displayVal, el.dateFormat || 'DD/MM/YYYY');
  } else if (el.type === 'serial') {
    const prefix = el.serialPrefix !== undefined ? el.serialPrefix : 'SN-';
    const step = el.serialStep || 1;
    const startNum = (el.serialStart || 1) + (previewRecordIndex * step);
    const padding = el.serialPadding || 4;
    displayVal = `${prefix}${String(startNum).padStart(padding, '0')}`;
  }

  if (el.prefix && !['serial', 'currency'].includes(el.type)) displayVal = `${el.prefix}${displayVal}`;
  if (el.suffix) displayVal = `${displayVal}${el.suffix}`;

  const shadowStyle = el.dropShadow ? `drop-shadow(2px 3px 4px ${el.shadowColor || 'rgba(0,0,0,0.4)'})` : 'none';

  return (
    <div
      id={`el-${el.id}`}
      onPointerDown={(e) => onPointerDown(e, el)}
      onContextMenu={(e) => onContextMenu(e, el.id)}
      onDoubleClick={(e) => onDoubleClick(e, el)}
      className={`absolute select-none ${isHandToolActive ? 'pointer-events-auto cursor-grab' : 'cursor-move'} ${
        isSelected && !isPreviewCanvasActive ? 'z-30' : ''
      } ${
        el.disabled || el.isSkipped ? 'border border-dashed border-rose-500/80 grayscale' : ''
      } ${
        el.isCutline ? 'border-2 border-dashed border-pink-500' : ''
      }`}
      style={{
        left: `${el.x}px`,
        top: `${el.y}px`,
        width: el.width ? `${el.width}px` : 'auto',
        height: el.height ? `${el.height}px` : 'auto',
        backgroundColor: (el.type !== 'path' && el.type !== 'pen' && el.backgroundColor && !el.isTransparentBg && el.backgroundColor !== 'transparent' && el.backgroundColor !== 'none') ? el.backgroundColor : undefined,
        borderRadius: (el.borderRadius ?? el.cornerRadius) ? `${el.borderRadius ?? el.cornerRadius}px` : undefined,
        padding: (el.type !== 'path' && el.type !== 'pen' && el.backgroundColor && !el.isTransparentBg && el.backgroundColor !== 'transparent' && el.backgroundColor !== 'none') ? '2px 6px' : undefined,
        opacity: (el.disabled || el.isSkipped) ? 0.35 : (el.opacity !== undefined ? el.opacity : 1),
        transform: `rotate(${el.rotation || 0}deg)`,
        filter: shadowStyle,
        zIndex: el.zIndex || 1
      }}
    >
      {/* Clean 0.5px Vector Selection Box (No Tint Fill) */}
      {isSelected && !isPreviewCanvasActive && (
        <div 
          className="export-ignore absolute inset-0 border-[0.5px] border-blue-500 pointer-events-none z-30"
          style={{ borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px` }}
        />
      )}

      {(el.disabled || el.isSkipped) && (
        <div className="absolute -top-2 -right-2 bg-rose-600 text-white p-0.5 rounded-full shadow-lg z-40 pointer-events-none flex items-center justify-center" title="Skipped / Disabled from print and output">
          <Ban className="w-3 h-3" />
        </div>
      )}

      {/* Text & TextBox Elements */}
      {(el.type === 'text' || el.type === 'textbox' || el.type === 'uppercase' || el.type === 'currency' || el.type === 'date' || el.type === 'serial') && (
        <div
          style={{
            fontSize: `${el.fontSize || 14}px`,
            color: el.color || '#000000',
            fontFamily: el.fontFamily || 'sans-serif',
            fontWeight: el.isBold || el.fontWeight === 'bold' ? 'bold' : 'normal',
            fontStyle: el.isItalic || el.fontStyle === 'italic' ? 'italic' : 'normal',
            textAlign: (el.textAlign as any) || 'left',
            letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : 'normal',
            lineHeight: el.lineHeight || 1.3,
            whiteSpace: (el.type === 'textbox' || el.isTextBox || el.wrapText) ? 'pre-wrap' : 'nowrap',
            wordBreak: (el.type === 'textbox' || el.isTextBox || el.wrapText) ? 'break-word' : 'normal',
            overflowWrap: 'break-word',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: (el.type === 'textbox' || el.isTextBox) ? 'flex-start' : 'center',
            justifyContent: el.textAlign === 'center' ? 'center' : (el.textAlign === 'right' ? 'flex-end' : 'flex-start')
          }}
        >
          {el.isCurved ? (
            <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 150} ${el.height || 40}`} className="overflow-visible">
              <path
                id={`curve-path-${el.id}`}
                d={`M 0 ${(el.height || 40) / 2} Q ${(el.width || 150) / 2} ${(el.height || 40) / 2 - (el.curveRadius || 100) / 4}, ${el.width || 150} ${(el.height || 40) / 2}`}
                fill="none"
              />
              <text fill={el.color || '#000000'} fontSize={el.fontSize || 14} fontFamily={el.fontFamily || 'sans-serif'} fontWeight={el.isBold ? 'bold' : 'normal'}>
                <textPath href={`#curve-path-${el.id}`} startOffset="50%" textAnchor="middle">
                  {displayVal || 'Curved Text'}
                </textPath>
              </text>
            </svg>
          ) : editingTextId === el.id ? (
            (el.type === 'textbox' || el.isTextBox || (displayVal && displayVal.includes('\n'))) ? (
              <textarea
                autoFocus
                defaultValue={displayVal}
                onPointerDown={(e) => e.stopPropagation()}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={(e) => {
                  if (setEditingTextId && onUpdateElement) {
                    const val = e.target.value;
                    onUpdateElement(el.id, { text: val, content: val, staticValue: val, isStatic: true, columnName: undefined });
                    setEditingTextId(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' || ((e.metaKey || e.ctrlKey) && e.key === 'Enter')) {
                    e.currentTarget.blur();
                  }
                }}
                style={{
                  textDecoration: el.textDecoration || 'none',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  textAlign: (el.textAlign as any) || 'left',
                  lineHeight: el.lineHeight || 1.3,
                  background: 'rgba(255,255,255,0.95)',
                  color: '#000',
                  outline: '2px solid #3b82f6',
                  border: 'none',
                  borderRadius: '3px',
                  margin: 0,
                  padding: '2px 4px',
                  width: '100%',
                  height: '100%',
                  resize: 'none',
                }}
              />
            ) : (
              <input
                autoFocus
                type="text"
                defaultValue={displayVal}
                onPointerDown={(e) => e.stopPropagation()}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={(e) => {
                  if (setEditingTextId && onUpdateElement) {
                    const val = e.target.value;
                    onUpdateElement(el.id, { text: val, content: val, staticValue: val, isStatic: true, columnName: undefined });
                    setEditingTextId(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.currentTarget.blur();
                  }
                }}
                style={{
                  textDecoration: el.textDecoration || 'none',
                  fontVariant: el.textVariant === 'small-caps' ? 'small-caps' : 'normal',
                  verticalAlign: el.verticalAlign || 'baseline',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  textAlign: (el.textAlign as any) || 'left',
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.95)',
                  color: '#000',
                  outline: '2px solid #3b82f6',
                  border: 'none',
                  borderRadius: '3px',
                  margin: 0,
                  padding: '1px 4px',
                  width: '100%',
                  height: '100%',
                }}
              />
            )
          ) : (
            <span
              style={{
                textDecoration: el.textDecoration || 'none',
                fontVariant: el.textVariant === 'small-caps' ? 'small-caps' : 'normal',
                verticalAlign: el.verticalAlign || 'baseline',
                fontSize: `${el.fontSize || 14}px`,
                color: el.color || '#000000',
                fontFamily: el.fontFamily || 'sans-serif',
                fontWeight: el.isBold || el.fontWeight === 'bold' ? 'bold' : 'normal',
                fontStyle: el.isItalic || el.fontStyle === 'italic' ? 'italic' : 'normal',
                letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : 'normal',
                whiteSpace: (el.type === 'textbox' || el.isTextBox || el.wrapText) ? 'pre-wrap' : 'nowrap',
                wordBreak: (el.type === 'textbox' || el.isTextBox || el.wrapText) ? 'break-word' : 'normal',
                width: (el.type === 'textbox' || el.isTextBox || el.wrapText) ? '100%' : 'auto',
                transform: `scaleX(${el.horizontalScale !== undefined ? el.horizontalScale / 100 : 1}) scaleY(${el.verticalScale !== undefined ? el.verticalScale / 100 : 1}) rotate(${el.textRotation || 0}deg) translateY(${-(el.baselineShift || 0)}px)`,
                display: 'inline-block',
                transformOrigin: 'center center',
                WebkitTextStroke: (el.textStrokeWidth || (el.borderWidth && el.borderWidth <= 2 && el.borderColor && el.borderColor !== 'transparent' && el.borderColor !== 'none'))
                  ? `${Math.min(el.textStrokeWidth || el.borderWidth || 1, 1.5)}px ${el.borderColor || '#000'}`
                  : undefined,
              }}
            >
              {displayVal || 'Text Element'}
            </span>
          )}
        </div>
      )}

      {/* QR Element */}
      {el.type === 'qr' && (
        <div
          className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
          style={{
            backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'),
            borderColor: el.borderColor || el.strokeColor || 'transparent',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: el.borderWidth ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        >
          <QrCodeSvg
            value={String(displayVal || 'https://example.com')}
            color={el.color || '#000000'}
            backgroundColor={el.backgroundColor || '#ffffff'}
            isTransparentBg={el.isTransparentBg}
            errorCorrection={el.qrErrorCorrection || 'M'}
            style={el.qrStyle || 'square'}
            width={el.width || 100}
            height={el.height || 100}
          />
        </div>
      )}

      {/* Barcode Elements */}
      {(el.type === 'barcode' || el.type === 'ean13' || el.type === 'upca') && (
        <div
          className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
          style={{
            backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'),
            borderColor: el.borderColor || el.strokeColor || 'transparent',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: el.borderWidth ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        >
          {el.type === 'ean13' ? (
            <Ean13BarcodeSvg
              value={String(displayVal || '5901234123457')}
              color={el.color || '#000000'}
              backgroundColor={el.backgroundColor || '#ffffff'}
              isTransparentBg={el.isTransparentBg}
              showText={el.showBarcodeText !== false}
              width={el.width || 160}
              height={el.height || 80}
            />
          ) : el.type === 'upca' ? (
            <UpcaBarcodeSvg
              value={String(displayVal || '012345678905')}
              color={el.color || '#000000'}
              backgroundColor={el.backgroundColor || '#ffffff'}
              isTransparentBg={el.isTransparentBg}
              showText={el.showBarcodeText !== false}
              width={el.width || 160}
              height={el.height || 80}
            />
          ) : displayVal ? (
            <ReactBarcode
              value={String(displayVal)}
              width={1.2}
              height={Math.max(20, (el.height || 60) - 15)}
              fontSize={10}
              lineColor={el.color || '#000000'}
              background={el.isTransparentBg ? 'rgba(0,0,0,0)' : (el.backgroundColor || '#ffffff')}
              displayValue={el.showBarcodeText !== false}
              margin={0}
            />
          ) : (
            <BarcodeIcon
              className="w-full h-full shrink-0"
              style={{ color: el.color || '#000000' }}
            />
          )}
        </div>
      )}

      {/* DataMatrix Element */}
      {el.type === 'datamatrix' && (
        <div
          className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
          style={{
            backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'),
            borderColor: el.borderColor || el.strokeColor || 'transparent',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: el.borderWidth ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        >
          <DataMatrixSvg
            value={String(displayVal || 'DATAMATRIX')}
            color={el.color || '#000000'}
            backgroundColor={el.backgroundColor || '#ffffff'}
            isTransparentBg={el.isTransparentBg}
            width={el.width || 80}
            height={el.height || 80}
          />
        </div>
      )}

      {/* Aztec Code Element */}
      {el.type === 'aztec' && (
        <div
          className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
          style={{
            backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'),
            borderColor: el.borderColor || el.strokeColor || 'transparent',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: el.borderWidth ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        >
          <AztecBarcodeSvg
            value={String(displayVal || 'AZTEC2026')}
            color={el.color || '#000000'}
            backgroundColor={el.backgroundColor || '#ffffff'}
            isTransparentBg={el.isTransparentBg}
            width={el.width || 80}
            height={el.height || 80}
          />
        </div>
      )}

      {/* PDF417 Element */}
      {el.type === 'pdf417' && (
        <div
          className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
          style={{
            backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'),
            borderColor: el.borderColor || el.strokeColor || 'transparent',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: el.borderWidth ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        >
          <Pdf417BarcodeSvg
            value={String(displayVal || 'PDF417-BATCH')}
            color={el.color || '#000000'}
            backgroundColor={el.backgroundColor || '#ffffff'}
            isTransparentBg={el.isTransparentBg}
            width={el.width || 140}
            height={el.height || 60}
          />
        </div>
      )}

      {/* Image Element */}
      {el.type === 'image' && (
        <img
          src={el.src || el.staticValue || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=300&q=80'}
          alt={el.name || 'Element image'}
          className={`w-full h-full ${
            el.objectFit === 'cover'
              ? 'object-cover'
              : el.objectFit === 'stretch'
              ? 'object-fill'
              : 'object-contain'
          }`}
          style={{ borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`, filter: (el.borderWidth && el.borderColor && el.borderColor !== 'transparent' ? `drop-shadow(${el.borderWidth}px 0 0 ${el.borderColor}) drop-shadow(-${el.borderWidth}px 0 0 ${el.borderColor}) drop-shadow(0 ${el.borderWidth}px 0 ${el.borderColor}) drop-shadow(0 -${el.borderWidth}px 0 ${el.borderColor})` : undefined) }}
        />
      )}

      {/* Rectangle Element */}
      {el.type === 'rectangle' && (
        <div
          className="w-full h-full"
          style={{
            backgroundColor: (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : (el.color === 'none' || el.fillColor === 'none' ? 'transparent' : '#e2e8f0'),
            borderColor: el.borderColor || el.strokeColor || '#94a3b8',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: (el.borderWidth || el.strokeWidth) ? 'solid' : 'none',
            borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`
          }}
        />
      )}

      {/* Circle Element */}
      {el.type === 'circle' && (
        <div
          className="w-full h-full"
          style={{
            borderRadius: '50%',
            backgroundColor: (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : (el.color === 'none' || el.fillColor === 'none' ? 'transparent' : '#e2e8f0'),
            borderColor: el.borderColor || el.strokeColor || '#94a3b8',
            borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
            borderStyle: (el.borderWidth || el.strokeWidth) ? 'solid' : 'none'
          }}
        />
      )}

      {/* Star Element */}
      {el.type === 'star' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getStarSvgPath(
              el.width || 100,
              el.height || 100,
              el.starPoints || 5,
              el.starInnerRadiusRatio || 0.4,
              el.cornerRadius ?? el.borderRadius ?? 0
            )}
            fill={el.fillColor || el.color || '#f59e0b'}
            stroke={el.borderColor || el.strokeColor || '#d97706'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Polygon Element */}
      {el.type === 'polygon' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getPolygonSvgPath(
              el.width || 100,
              el.height || 100,
              el.polygonSides || 6,
              el.cornerRadius ?? el.borderRadius ?? 0
            )}
            fill={el.fillColor || el.color || '#3b82f6'}
            stroke={el.borderColor || el.strokeColor || '#1d4ed8'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Triangle Element */}
      {el.type === 'triangle' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getTriangleSvgPath(el.width || 100, el.height || 100)}
            fill={el.fillColor || el.color || '#3b82f6'}
            stroke={el.borderColor || el.strokeColor || '#1d4ed8'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Diamond Element */}
      {el.type === 'diamond' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getDiamondSvgPath(el.width || 100, el.height || 100)}
            fill={el.fillColor || el.color || '#3b82f6'}
            stroke={el.borderColor || el.strokeColor || '#1d4ed8'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Heart Element */}
      {el.type === 'heart' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getHeartSvgPath(el.width || 100, el.height || 100)}
            fill={el.fillColor || el.color || '#ec4899'}
            stroke={el.borderColor || el.strokeColor || '#be185d'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Arrow Element */}
      {el.type === 'arrow' && (
        <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
          <path
            d={getArrowSvgPath(el.width || 100, el.height || 100)}
            fill={el.fillColor || el.color || '#3b82f6'}
            stroke={el.borderColor || el.strokeColor || '#1d4ed8'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
          />
        </svg>
      )}

      {/* Line Element */}
      {el.type === 'line' && (
        <svg width="100%" height="100%" className="overflow-visible">
          {renderArrowheadDefs(el)}
          <path
            d={getLineEffectSvgPath(el.width || 100, el.height || 20, el.lineEffect || 'none', el.effectAmplitude, el.effectFrequency, el.effectPointsStyle)}
            fill="none"
            stroke={el.borderColor || el.strokeColor || '#000000'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 2}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
            markerStart={el.arrowheadStart && el.arrowheadStart !== 'none' ? `url(#arrow-start-${el.id})` : undefined}
            markerEnd={el.arrowheadEnd && el.arrowheadEnd !== 'none' ? `url(#arrow-end-${el.id})` : undefined}
          />
        </svg>
      )}

      {/* Vector Path / Pen Element */}
      {(el.type === 'path' || el.type === 'pen') && (
        <svg width="100%" height="100%" className="overflow-visible">
          {renderArrowheadDefs(el)}
          <path
            d={el.pathData || el.pathD || (el.points ? pointsToSvgPath(el.points, el.closedPath, el.isSmooth) : '')}
            fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.backgroundColor && el.backgroundColor !== 'none' && !el.isTransparentBg) ? el.backgroundColor : (el.color && el.color !== 'none') ? el.color : '#3b82f6'}
            fillRule={el.fillRule || 'nonzero'}
            stroke={el.borderColor || el.strokeColor || '#1d4ed8'}
            strokeWidth={el.borderWidth ?? el.strokeWidth ?? 2}
            strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
            strokeLinecap={el.lineCap || 'round'}
            strokeLinejoin={el.lineJoin || 'miter'}
            strokeMiterlimit={el.strokeMiterLimit ?? 10}
            markerStart={el.arrowheadStart && el.arrowheadStart !== 'none' ? `url(#arrow-start-${el.id})` : undefined}
            markerEnd={el.arrowheadEnd && el.arrowheadEnd !== 'none' ? `url(#arrow-end-${el.id})` : undefined}
          />
        </svg>
      )}

      {/* Interactive Bezier & Anchor Node Handles */}
      {isSelected && (el.type === 'path' || el.type === 'pen') && el.points && el.points.length > 0 && (
        <svg className="export-ignore absolute inset-0 w-full h-full overflow-visible pointer-events-none z-40">
          {el.points.map((pt, idx) => {
            const isPointSelected = selectedAnchorIdx === idx || (selectedAnchorIdx === null && idx === 0);
            const { dirX, dirY } = getAnchorTangent(el.points!, idx, el.closedPath, 28);
            const activeCp1 = pt.cp1 || (isPointSelected ? { x: Math.round(pt.x - dirX), y: Math.round(pt.y - dirY) } : undefined);
            const activeCp2 = pt.cp2 || (isPointSelected ? { x: Math.round(pt.x + dirX), y: Math.round(pt.y + dirY) } : undefined);
            const showHandles = isPointSelected || Boolean(pt.cp1 || pt.cp2);

            return (
              <g key={idx} className="pointer-events-auto">
                {showHandles && activeCp1 && (
                  <g>
                    <line x1={pt.x} y1={pt.y} x2={activeCp1.x} y2={activeCp1.y} stroke="#2563eb" strokeWidth={0.5} />
                    {/* Generous invisible hit area for easy grabbing */}
                    <circle
                      cx={activeCp1.x}
                      cy={activeCp1.y}
                      r={8}
                      fill="transparent"
                      className="cursor-pointer"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setSelectedAnchorIdx(idx);
                        if (!pt.cp1) {
                          onEnsureAnchorHandles?.(el, idx);
                        }
                        onControlPointDrag?.(e, el, idx, 'cp1');
                      }}
                      title="Drag Bezier incoming control handle"
                    />
                    {/* Crisp 0.5px visual circle */}
                    <circle
                      cx={activeCp1.x}
                      cy={activeCp1.y}
                      r={2}
                      fill="#ffffff"
                      stroke="#2563eb"
                      strokeWidth={0.5}
                      pointerEvents="none"
                      className="hover:fill-blue-600 transition-colors"
                    />
                  </g>
                )}
                {showHandles && activeCp2 && (
                  <g>
                    <line x1={pt.x} y1={pt.y} x2={activeCp2.x} y2={activeCp2.y} stroke="#2563eb" strokeWidth={0.5} />
                    {/* Generous invisible hit area for easy grabbing */}
                    <circle
                      cx={activeCp2.x}
                      cy={activeCp2.y}
                      r={8}
                      fill="transparent"
                      className="cursor-pointer"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setSelectedAnchorIdx(idx);
                        if (!pt.cp2) {
                          onEnsureAnchorHandles?.(el, idx);
                        }
                        onControlPointDrag?.(e, el, idx, 'cp2');
                      }}
                      title="Drag Bezier outgoing control handle"
                    />
                    {/* Crisp 0.5px visual circle */}
                    <circle
                      cx={activeCp2.x}
                      cy={activeCp2.y}
                      r={2}
                      fill="#ffffff"
                      stroke="#2563eb"
                      strokeWidth={0.5}
                      pointerEvents="none"
                      className="hover:fill-blue-600 transition-colors"
                    />
                  </g>
                )}

                {/* Generous invisible hit target (14x14px) so clicking anchor point NEVER misclicks parent element */}
                <rect
                  x={pt.x - 7}
                  y={pt.y - 7}
                  width={14}
                  height={14}
                  fill="transparent"
                  className="cursor-move"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedAnchorIdx(idx);
                    if (!pt.cp1 && !pt.cp2) {
                      onEnsureAnchorHandles?.(el, idx);
                    }
                    onAnchorPointDrag?.(e, el, idx);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedAnchorIdx(idx);
                    if (!pt.cp1 && !pt.cp2) {
                      onEnsureAnchorHandles?.(el, idx);
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleAnchorPointType?.(el, idx);
                  }}
                  title="Anchor point (0.5px). Click to display Bezier handles. Drag to move. Double-click to toggle smooth/corner."
                />

                {/* Crisp 0.5px hairline visual anchor square (4x4px) */}
                <rect
                  x={pt.x - 2}
                  y={pt.y - 2}
                  width={4}
                  height={4}
                  fill={isPointSelected ? "#2563eb" : (pt.cp1 || pt.cp2 ? "#3b82f6" : "#ffffff")}
                  stroke="#1d4ed8"
                  strokeWidth={0.5}
                  rx={0.5}
                  pointerEvents="none"
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* Vector Selection Bounding Box & Control Handles (Only shown during single selection to prevent multi-select clutter) */}
      {isSelected && isSingleSelection && !el.locked && !isPreviewCanvasActive && (
        <div className="export-ignore absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
          {/* Top Rotation Handle Pin & Stem */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-[0.5px] h-3 bg-blue-500 pointer-events-none z-35" />
          <div
            onPointerDown={(e) => onRotateStart(e, el)}
            className="absolute -top-4.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-[0.5px] border-blue-500 rounded-full pointer-events-auto cursor-grab z-40 flex items-center justify-center hover:scale-125 transition-transform shadow-xs"
            title="Rotate Element"
          >
            <div className="w-0.5 h-0.5 rounded-full bg-blue-500" />
          </div>

          {/* 8 Small Crisp Square Resize Handles (Only for shapes without inline vector anchor nodes to prevent clutter) */}
          {!((el.type === 'path' || el.type === 'pen') && el.points && el.points.length > 0) && (
            <>
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'nw')}
                className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                title="Resize Top-Left"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'n')}
                className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-ns-resize z-40 hover:scale-125 transition-transform"
                title="Resize Top"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'ne')}
                className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                title="Resize Top-Right"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'w')}
                className="absolute top-1/2 -left-0.5 -translate-y-1/2 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-ew-resize z-40 hover:scale-125 transition-transform"
                title="Resize Left"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'e')}
                className="absolute top-1/2 -right-0.5 -translate-y-1/2 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-ew-resize z-40 hover:scale-125 transition-transform"
                title="Resize Right"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'sw')}
                className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                title="Resize Bottom-Left"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 's')}
                className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-ns-resize z-40 hover:scale-125 transition-transform"
                title="Resize Bottom"
              />
              <div
                onPointerDown={(e) => onResizeStart(e, el, 'se')}
                className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-500 pointer-events-auto cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                title="Resize Bottom-Right"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Performance optimization: prevent unnecessary re-renders of template elements
  // unless properties that actually affect visual output or interaction state change.
  
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isPreviewCanvasActive !== nextProps.isPreviewCanvasActive) return false;
  if (prevProps.isHandToolActive !== nextProps.isHandToolActive) return false;
  if (prevProps.showLivePreview !== nextProps.showLivePreview) return false;
  if (prevProps.previewRecordIndex !== nextProps.previewRecordIndex) return false;
  if (prevProps.editingTextId !== nextProps.editingTextId) return false;
  if (prevProps.isSingleSelection !== nextProps.isSingleSelection) return false;

  // Shallow compare the element data for visual changes
  const p = prevProps.el;
  const n = nextProps.el;
  if (p.id !== n.id || p.type !== n.type || p.x !== n.x || p.y !== n.y || p.width !== n.width || p.height !== n.height || p.rotation !== n.rotation) return false;
  if (p.color !== n.color || p.backgroundColor !== n.backgroundColor || p.borderColor !== n.borderColor || p.borderWidth !== n.borderWidth) return false;
  if (p.fontSize !== n.fontSize || p.fontFamily !== n.fontFamily || p.isBold !== n.isBold || p.isItalic !== n.isItalic || p.textAlign !== n.textAlign) return false;
  if (p.fontWeight !== n.fontWeight || p.fontStyle !== n.fontStyle || p.textDecoration !== n.textDecoration || p.textVariant !== n.textVariant) return false;
  if (p.content !== n.content || p.text !== n.text || p.columnName !== n.columnName || p.opacity !== n.opacity || p.zIndex !== n.zIndex) return false;
  if (p.disabled !== n.disabled || p.isSkipped !== n.isSkipped || p.locked !== n.locked) return false;
  if (p.borderRadius !== n.borderRadius || p.cornerRadius !== n.cornerRadius || p.dropShadow !== n.dropShadow) return false;
  
  // Barcode / QR specific
  if (p.qrErrorCorrection !== n.qrErrorCorrection || p.qrStyle !== n.qrStyle || p.showBarcodeText !== n.showBarcodeText) return false;
  
  // Vector Points comparison (only for path/pen)
  if (p.points !== n.points) {
    if (!p.points || !n.points) return false;
    if (p.points.length !== n.points.length) return false;
    // Fast path for large point arrays: check a few samples first
    if (p.points.length > 50) {
      if (p.points[0].x !== n.points[0].x || p.points[p.points.length-1].x !== n.points[n.points.length-1].x) return false;
    }
    if (JSON.stringify(p.points) !== JSON.stringify(n.points)) return false;
  }
  if (p.pathData !== n.pathData) return false;

  // If CSV data changed, check if the specific value used by this element changed
  if (nextProps.showLivePreview && nextProps.el.columnName) {
    const col = nextProps.el.columnName;
    const idx = nextProps.previewRecordIndex;
    const prevVal = prevProps.csvData?.rows?.[idx]?.[col];
    const nextVal = nextProps.csvData?.rows?.[idx]?.[col];
    if (prevVal !== nextVal) return false;
  }

  return true;
});

CanvasElementItem.displayName = 'CanvasElementItem';
